import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useExcludeSpecialCommission } from "../../../../../common/api/queries";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import type { UUID } from "../../../../../typings/global";
import { SpecialCommission } from "../../../special/types";
import { ExcludeSpecialCommission } from "../../types";

interface Props {
  batchId: UUID;
  agentId: UUID;
  commission?: SpecialCommission;
  onFormCancel: () => void;
}

export const SpecialCommissionExcludeForm = ({ commission, batchId, agentId, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<ExcludeSpecialCommission>();

  const excludeSpecialCommission = useExcludeSpecialCommission();

  useEffect(() => {
    if (commission) {
      form.setFieldsValue({ optimisticLockVersion: commission.optimisticLockVersion });
    }
  }, [commission, form]);

  const handleFormSubmit = () => {
    if (commission) {
      form
        .validateFields()
        .then((values) =>
          excludeSpecialCommission.mutate(
            { id1: batchId, id2: agentId, id3: commission.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "commissions.special.attrs", error.violations),
            },
          ),
        )
        .catch(resolveFormValidationError);
    }
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={!!commission}
      title={t("commissions.batch.titles.excludeSpecialCommission")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={excludeSpecialCommission.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="excludeSpecialCommissionForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="exclusionReason"
              label={t("commissions.special.attrs.exclusionReason")}
              rules={[validations.notBlank, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
